import {ChangeDetectionStrategy, Component, computed, effect, Inject, inject, OnDestroy, OnInit} from '@angular/core';
import {NavigationEnd, Router, RouterOutlet} from '@angular/router';
import {HttpClient} from '@angular/common/http';
import {Store} from '@ngrx/store';
import * as fromRoot from './shared/store/app.reducers';
import {getUserState} from './shared/store/app.reducers';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {AuthenticationService} from './shared/service/authentication.service';
import {TranslateService} from '@ngx-translate/core';
import {SystemSettingsService} from './shared/service/system-settings.service';
import {UpdateViewSettings} from './shared/store/offer/offer.actions';
import {UpdateUserInfoAction} from './shared/store/user/user.actions';
import {AppConfig} from './shared/model/app-config.model';
import {APP_CONFIG} from './shared/misc/inject-tokens';
import {datadogRum} from '@datadog/browser-rum';
import {BarAlertSignalStore} from './shared/store/alert/bar-alert.signal-store';
import {UserSignalStore} from './shared/store/user/user.signal-store';
import {SearchService} from './shared/service/search.service';
import {SpinnerComponent} from './shared/component/spinner/spinner.component';
import {NgClass} from '@angular/common';
import {IconComponent} from './shared/component/icon/icon.component';
import {TopMenuComponent} from './shared/component/top-menu/top-menu.component';
import {ToastContainerComponent} from './standalone/toast-container/toast-container.component';
import {
  DocumentMissingMessageComponent
} from './shared/component/document-missing-message/document-missing-message.component';
import {DealerLockedMessageComponent} from './shared/component/dealer-locked-message/dealer-locked-message.component';
import {NgbAlert} from '@ng-bootstrap/ng-bootstrap';
import {BrowserAlertDialogComponent} from './shared/component/browser-alert-dialog/browser-alert-dialog.component';
import {FooterComponent} from './shared/component/footer/footer.component';
import {NgxFloatUiModule} from 'ngx-float-ui';

@Component({
  selector: 'ucs-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgxFloatUiModule,
    SpinnerComponent,
    NgClass,
    IconComponent,
    TopMenuComponent,
    ToastContainerComponent,
    DocumentMissingMessageComponent,
    DealerLockedMessageComponent,
    NgbAlert,
    RouterOutlet,
    BrowserAlertDialogComponent,
    FooterComponent,
  ]
})
export class AppComponent implements OnInit, OnDestroy {
  public showTopMenuAndFooter = true;
  title = 'Used Car Sales';
  currentYear = new Date().getFullYear();
  userInfo: UserInfoDto;
  stage: string;
  version: string;
  isMSClarityEnabled = false;
  expandedViewport: boolean;

  isLastDomainAccessEnabled = computed(() =>
    this.userSignalStore.userInfo() && this.userSignalStore.isFeatureActivatedForAnyChannel('UCS_723_LAST_DOMAIN_ACCESS'));

  alertStore = inject(BarAlertSignalStore);
  userSignalStore = inject(UserSignalStore);
  private readonly unsubscribe: Subject<void> = new Subject<void>();
  private readonly store = inject(Store<fromRoot.AppState>);
  private readonly router = inject(Router);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly translateService = inject(TranslateService);
  private readonly systemSettingsService = inject(SystemSettingsService);
  private readonly http = inject(HttpClient);
  private readonly searchService = inject(SearchService);

  // keep constructor injection for AppConfig, as this is fastest
  /* eslint-disable @angular-eslint/prefer-inject */
  constructor(@Inject(APP_CONFIG) private config: AppConfig) {
    effect(() => {
      if (this.userSignalStore.locale()) {
        this.translateService.use(this.userSignalStore.locale());
      }
    });
  }

  ngOnInit() {
    this.http.get(`${this.config.authenticationUrl}/actuator/info`)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((actuatorInfo: any) => {
        const stage = this.determineStage();
        this.version = actuatorInfo.git.commit.id;
        if (this.config.datadogRumEnabled) {
          this.setupDatadogRum(actuatorInfo, stage);
        }
      });

    this.updateUserLocal();

    this.router.events
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(event => { // subscribe to change of route parameters
        if (event instanceof NavigationEnd) {
          if (event.url.endsWith('logout')
            || event.url.endsWith('forbidden')
            || event.url.endsWith('auth')
            || event.url.endsWith('landingpage')
            || event.url.endsWith('auth/failure')) {
            this.showTopMenuAndFooter = false;
          }
          this.expandedViewport = event.url.endsWith('maintenance/vehicle') || event.url.includes('maintenance/vehicle#');
        }
      });

    if (this.authenticationService.isAuthenticated()) {
      this.userSignalStore.fetchUserInfo();
    }
    this.updateViewSettingsInStore();
    this.subscribeToUserState();
    this.subscribeToUserInfo();
  }

  public getBrowserName(): string {
    const agent = window.navigator.userAgent.toLowerCase();
    switch (true) {
    case agent.indexOf('edge') > -1:
      return 'edge';
    case agent.indexOf('edg/') > -1:
      return 'chromium based';
    case agent.indexOf('opr') > -1 && !!(<any>window).opr:
      return 'opera';
    case agent.indexOf('chrome') > -1 && !!(<any>window).chrome:
      return 'chrome';
    case agent.indexOf('trident') > -1:
      return 'ie';
    case agent.indexOf('firefox') > -1:
      return 'firefox';
    case agent.indexOf('safari') > -1:
      if (agent.indexOf('crios') > -1) {
        return 'chrome';
      }
      return 'safari';
    default:
      return 'other';
    }
  }

  loadMSClarityScript() {
    if (document.getElementById('ms-clarity-script')) {
      //Script already present
      return;
    }
    const node = document.createElement('script'); // creates the script tag
    // set the source (insert url in between quotes)
    node.id = 'ms-clarity-script';
    node.innerHTML = '(function(c,l,a,r,i,t,y){ c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)}; ' +
      't=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i; ' +
      'y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y); })' +
      '(window, document, "clarity", "script", "crjkzzx3db");';
    // set the script type
    node.type = 'text/javascript';
    // makes script run asynchronously
    node.async = true;
    // append to head of document
    document.getElementsByTagName('head')[0].appendChild(node);
  }

  setStageInfo() {
    this.systemSettingsService
      .isSystemFeatureActivatedForAnyChannel('SHOW_STAGE_INFO')
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(showStageInfo => {
        if (showStageInfo) {
          if (window.location.hostname.includes('localhost')) {
            this.stage = 'local';
          } else if (window.location.hostname.includes('entw') || window.location.hostname.includes('dev')) {
            this.stage = 'dev';
          } else if (window.location.hostname.includes('qa')) {
            this.stage = 'qa';
          } else if (window.location.hostname.includes('demo')) {
            this.stage = 'demo';
          } else {
            this.stage = 'prod';
          }
        }
      });
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
    if (this.authenticationService.isAuthenticated()) {
      this.authenticationService.disconnectWebsockets();
    }
  }

  close() {
    this.alertStore.updateAlert(false, undefined);
  }

  private subscribeToUserInfo() {
    this.store.select(fromRoot.getUserInfo)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(userInfo => {
        if (this.authenticationService.isAuthenticated()) {
          if (userInfo) {
            this.setStageInfo();
            this.userInfo = userInfo;
            this.authenticationService.connectWebsockets();
          } else {
            this.store.dispatch(new UpdateUserInfoAction());
          }
        }
      });
  }

  private subscribeToUserState() {
    this.store.select(getUserState)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(userState => {
        if (this.authenticationService.isAuthenticated()) {
          if (userState) {

            // init distribution channel in store on first user state update
            const channels = AuthenticationService.getPermittedChannelsToBuyOffers(userState);
            if (channels && channels.length > 0) {
              this.searchService.expiringOffersChannel.set(channels[0]);
              this.searchService.newOffersChannel.set(channels[0]);
            }

            this.isMSClarityEnabled = this.systemSettingsService.isSystemFeatureActivatedForAnyChannelOfState(
              'ENABLE_MS_CLARITY', userState);
            if (this.isMSClarityEnabled) {
              this.loadMSClarityScript();
            }
          }
        }
      });
  }

  private determineStage(): string {
    const hostname = window.location.hostname;
    if (hostname.includes('entw') || hostname.includes('dev')) {
      return 'dev';
    } else if (hostname.includes('qa')) {
      return 'qa';
    } else {
      return 'prod';
    }
  }

  private setupDatadogRum(info: any, stage: string) {
    datadogRum.init({
      applicationId: '618f2bb3-4c2b-4fe0-9fef-5ebd497a71ae',
      clientToken: 'pub86519919cc7b95bf0ab6ec684b87613c',
      site: 'datadoghq.eu',
      service: 'ucs-frontend-nginx',
      env: stage,
      // Specify a version number to identify the deployed version of your application in Datadog
      version: info.git.commit.id,
      allowedTracingUrls: [/https:\/\/.*\.ucs-phs\.com/],
      sessionSampleRate: 100,
      sessionReplaySampleRate: 0, // DO NOT ENABLE - GDPR NIGHTMARE - AND MONEY
      traceSampleRate: 20,
      traceContextInjection: 'all',
      trackUserInteractions: true,
      startSessionReplayRecordingManually: true,
      trackResources: true,
      trackLongTasks: true,
      // https://docs.datadoghq.com/real_user_monitoring/session_replay/privacy_options/?tab=maskuserinput
      // der Privacy Level kann auch HTML Item Ebene jeweil geändert werden
      defaultPrivacyLevel: 'mask-user-input',
      propagateTraceBaggage: false, // v7 default changed to true; keep same behavior for now
      telemetrySampleRate: 0
    });

    datadogRum.setGlobalContextProperty('team', 'whs-dib-ucca');
  }

  private updateViewSettingsInStore() {
    // get view-settings from local storage and initially propagate to redux store
    let activeTags = <ViewTag[]>['MILEAGE', 'INITIAL-REGISTRATION', 'FUEL', 'POWER'];
    try {
      const items = localStorage.getItem('ucs-view-settings');
      if (items !== null) {
        activeTags = JSON.parse(items);
      }
    } catch (e) {
      console.debug(e);
      //intentionally left blank
    }
    this.store.dispatch(new UpdateViewSettings(activeTags));
  }

  private updateUserLocal() {
    if (this.translateService.getBrowserLang() === 'de'
      || this.translateService.getBrowserLang() === 'de-de-key') {
      this.userSignalStore.updateUserLocale(this.translateService.getBrowserLang());
    } else {
      this.userSignalStore.updateUserLocale('en');
    }
  }
}
